import { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router";
import { useOrgContext } from "../context/OrgContext";
import { useAuth } from "../context/AuthContext";
import { RefreshButton } from "../components/ui/RefreshButton";
import { useLocale } from "../context/LanguageContext";
import {
  OrdersStatsBar,
  OrdersTable,
  FulfillOrderDialog,
  CancelOrderDialog,
  OrderDetailSheet,
  ExportOrdersButton,
  useOrganizationOrders,
  useOrganizationOrderStats,
  useFulfillOrder,
  useCancelOrder,
} from "../features/marketplace";
import type { MarketplaceOrder, OrderStatus } from "../features/marketplace";

type TabKey = "PENDING" | "FULFILLED" | "ALL";

const PAGE_SIZE = 10;

const TABS: { key: TabKey; labelKey: string }[] = [
  { key: "PENDING", labelKey: "orders.tabPending" },
  { key: "FULFILLED", labelKey: "orders.tabFulfilled" },
  { key: "ALL", labelKey: "orders.tabAll" },
];

const VALID_TABS = new Set<TabKey>(["PENDING", "FULFILLED", "ALL"]);

function tabToStatus(tab: TabKey): OrderStatus | undefined {
  if (tab === "PENDING") return "PENDING";
  if (tab === "FULFILLED") return "FULFILLED";
  return undefined;
}

export function OrdersPage() {
  const { currentOrgId, currentOrg } = useOrgContext();
  const { user } = useAuth();
  const { t } = useLocale();
  const isOrgAdmin = user?.role === "ORG_ADMIN";

  // ─── URL param ↔ tab (bidirectional for deep links) ─────────────
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab")?.toUpperCase() as TabKey | undefined;
  const initialTab: TabKey = tabParam && VALID_TABS.has(tabParam) ? tabParam : "PENDING";

  // ─── Tab & Pagination State ──────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [page, setPage] = useState(0);

  const handleTabChange = useCallback((tab: TabKey) => {
    setActiveTab(tab);
    setSearchParams(tab !== "PENDING" ? { tab } : {}, { replace: true });
  }, [setSearchParams]);

  // Reset to first page on tab change
  useEffect(() => {
    setPage(0);
  }, [activeTab]);

  // ─── Data Fetching ───────────────────────────────────────────────────
  const { data, loading, refetch } = useOrganizationOrders({
    organizationId: currentOrgId ?? "",
    status: tabToStatus(activeTab),
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const orders = data?.organizationOrders?.orders ?? [];
  const total = data?.organizationOrders?.total ?? 0;

  // ─── Order stats (server-computed) ──────────────────────────────────
  const { data: statsData, loading: statsLoading } = useOrganizationOrderStats(
    currentOrgId ?? "",
  );

  // ─── Mutations ───────────────────────────────────────────────────────
  const [fulfillOrder, { loading: fulfilling }] = useFulfillOrder();
  const [cancelOrder, { loading: cancelling }] = useCancelOrder();

  // ─── Dialog State ────────────────────────────────────────────────────
  const [fulfillTarget, setFulfillTarget] =
    useState<MarketplaceOrder | null>(null);
  const [cancelTarget, setCancelTarget] =
    useState<MarketplaceOrder | null>(null);
  const [detailTarget, setDetailTarget] =
    useState<MarketplaceOrder | null>(null);

  // ─── Handlers ────────────────────────────────────────────────────────
  const handleFulfill = useCallback(
    async (fulfillmentDetails: string) => {
      if (!fulfillTarget) return;
      try {
        await fulfillOrder({
          variables: {
            orderId: fulfillTarget._id,
            fulfillmentDetails: fulfillmentDetails || undefined,
          },
        });
        console.info("[OrdersPage] Order fulfilled:", fulfillTarget._id);
        setFulfillTarget(null);
      } catch (err) {
        console.error("[OrdersPage] Fulfill failed:", err);
      }
    },
    [fulfillTarget, fulfillOrder],
  );

  const handleCancel = useCallback(
    async (reason: string) => {
      if (!cancelTarget) return;
      try {
        await cancelOrder({
          variables: {
            orderId: cancelTarget._id,
            reason,
          },
        });
        console.info("[OrdersPage] Order cancelled:", cancelTarget._id);
        setCancelTarget(null);
      } catch (err) {
        console.error("[OrdersPage] Cancel failed:", err);
      }
    },
    [cancelTarget, cancelOrder],
  );

  // ─── Guard: no org selected (ORG_ADMIN) ─────────────────────────────
  if (isOrgAdmin && (!currentOrgId || !currentOrg)) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-lg font-medium text-foreground">
          {t("common.selectOrg")}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("common.selectOrgHint")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("orders.title")}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {currentOrg
              ? t("orders.descriptionOrg", { orgName: currentOrg.name })
              : t("orders.descriptionAll")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {currentOrgId && currentOrg && (
            <ExportOrdersButton
              organizationId={currentOrgId}
              orgSlug={currentOrg.slug ?? currentOrg.name}
              statusFilter={tabToStatus(activeTab)}
            />
          )}
          <RefreshButton
            onRefresh={async () => { await refetch(); }}
            loading={loading || statsLoading}
          />
        </div>
      </div>

      {/* Stats Bar */}
      <OrdersStatsBar stats={statsData?.organizationOrderStats} isLoading={statsLoading} />

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => handleTabChange(tab.key)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t(tab.labelKey)}
            {tab.key === "PENDING" && activeTab === "PENDING" && !loading && total > 0 && (
              <span className="ml-1.5 inline-flex min-w-[20px] items-center justify-center rounded-full bg-warning/10 px-1.5 py-0.5 text-xs font-semibold text-warning">
                {total}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Orders Table */}
      <OrdersTable
        orders={orders}
        total={total}
        isLoading={loading}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        onFulfill={setFulfillTarget}
        onCancel={setCancelTarget}
        onViewDetails={setDetailTarget}
      />

      {/* Fulfill Dialog */}
      {fulfillTarget && (
        <FulfillOrderDialog
          order={fulfillTarget}
          onFulfill={handleFulfill}
          onClose={() => setFulfillTarget(null)}
          loading={fulfilling}
        />
      )}

      {/* Cancel Dialog */}
      {cancelTarget && (
        <CancelOrderDialog
          order={cancelTarget}
          onCancel={handleCancel}
          onClose={() => setCancelTarget(null)}
          loading={cancelling}
        />
      )}

      {/* Detail Sheet */}
      {detailTarget && (
        <OrderDetailSheet
          order={detailTarget}
          onClose={() => setDetailTarget(null)}
        />
      )}
    </div>
  );
}
